import {
  AnimationClip,
  InterpolateDiscrete,
  InterpolateSmooth,
  Matrix4,
  NormalAnimationBlendMode,
  Quaternion,
  QuaternionKeyframeTrack,
  Vector3,
  VectorKeyframeTrack,
} from 'three';

import { Camera } from '@react-three/fiber';
import { SurfacePathTravel } from '@spazious/dollhouse-three';

/**
 * Creates animation clip for camera to follow a path.
 *
 *
 * @see https://github.com/nytimes/three-story-controls/blob/f839ff3c87302e6e56af221949dbe5c64f4d1a39/src/camerahelper/index.ts#L247
 * @param camera
 * @param pathTravel
 * @param duration
 * @param eyeHeight
 * @param useSlerp
 */
export const createCameraAnimation = (
  camera: Camera,
  pathTravel: SurfacePathTravel,
  eyeHeight: number = 1.5,
  duration?: number
) => {
  const positionValues: number[] = [];
  const quaternionValues: number[] = [];
  const times: number[] = [];

  const tmpPosition = new Vector3();
  const tmpQuaternion = new Quaternion();
  const tmpMatrix = new Matrix4();

  const eyeVector = camera.up.clone().multiplyScalar(eyeHeight);

  const pathLength = pathTravel.getLength();
  const stepsPerMeter = 100;
  const secondsPerMeter = 1;
  const steps = Math.floor(pathLength * stepsPerMeter);
  const animDuration = duration ?? pathLength * secondsPerMeter;

  let currentTime = 0;
  const lerpAmount = 1 / steps;
  for (let i = 0; i < steps; i++) {
    const { inner, outer } = pathTravel.getPointsAt(currentTime);

    tmpPosition.copy(outer).add(eyeVector);
    tmpQuaternion.setFromRotationMatrix(tmpMatrix.lookAt(outer, inner, camera.up));
    tmpPosition.toArray(positionValues, positionValues.length);
    tmpQuaternion.toArray(quaternionValues, quaternionValues.length);
    times.push(currentTime * animDuration);
    currentTime += lerpAmount;
  }

  return new AnimationClip(
    'CameraPath',
    animDuration,
    [
      new VectorKeyframeTrack('.position', times, positionValues, InterpolateSmooth),
      new QuaternionKeyframeTrack('.quaternion', times, quaternionValues, InterpolateDiscrete),
    ],
    NormalAnimationBlendMode
  );
};
